import React, { useState, useEffect, Fragment, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid, Divider, makeStyles, Box, Typography } from '@material-ui/core';
import { useRouteMatch } from 'react-router';

import { useProjectApi } from 'src/services';
import { AnalyticCard } from 'src/scenes/Project/components';
import { ConcernSummary, GeospatialAnalytic, LoadingBox } from 'src/scenes/Project/scenes/Dashboard/components';
import { useFetchStatus } from 'src/hooks';
import { getLeafletOptionsFn } from 'src/scenes/Project/scenes/Analytics/smaAnalyticsHelper';
import { WebContext } from 'src/scenes/web-context';
import { ContentContainer, TitleContainer } from 'src/components';
import { processComponentConcerns, componentConfig } from 'src/utils/natureStateIndicatorsHelper';


const useStyles = makeStyles(theme => ({
  divider: {
    width: '100%',
    marginBottom: theme.spacing(1),
  },
  indicatorLabel: {
    fontStyle: 'italic',
    display: 'inline',
  },
}));

const landUseWater = [ 'landcover-water' ];

const FreshwaterContainer = ({ interestArea, project }) => {
  const match = useRouteMatch();
  const classes = useStyles();
  const projectApi = useProjectApi();
  const { currentUser: { token } } = useContext(WebContext);

  const [ geospatialAnalytic, setGeospatialAnalytic ] = useState([]);
  const [ geospatialOptions, setGeospatialOptions ] = useState({
    spectralIndicesOptions: [],
  });

  let { projectId, siteId } = match.params;
  const isSite = Boolean(siteId);
  projectId = isSite ? siteId : projectId;

  const componentId = match.url.split('/').filter(Boolean).pop();

  const [ concerns, setConcerns ] = useState([]);

  const { handleConfirm: fetchConcerns, isLoading: isLoadingConcerns, isSuccess: isSuccessConcerns } = useFetchStatus({
    confirmFn: async () => {
      const indicators = await projectApi.getNatureStateIndicators({ projectId });
      const processedConcerns = processComponentConcerns(indicators, 'freshwater', 2024);

      const concerns = processedConcerns.map(concern => {
        const indicatorConfig = componentConfig.freshwater.indicators[concern.fieldKey];
        return {
          ...concern,
          tooltip: indicatorConfig.tooltip,
          url: indicatorConfig.url,
          ReasonComponent: () =>
            <>
              basado en el
              <Typography className={classes.indicatorLabel} variant="body2" component="span">
                &nbsp;{indicatorConfig.description}&nbsp;
              </Typography>
              del indicador
              <Typography className={classes.indicatorLabel} variant="body2" component="span">
                &nbsp;{indicatorConfig.fullName} ({indicatorConfig.shortName})
              </Typography>
            </>,
        };
      });

      setConcerns(concerns);
    },
  });

  useEffect(() => {
    fetchConcerns();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    handleConfirm: fetchGeospatialAnalytics,
    isLoading: isLoadingGeospatialAnalytics,
    isSuccess: isSuccessGeospatialAnalytics,
  } = useFetchStatus({
    confirmFn: async () => {
      const [ geospatialAnalytic, geospatialOptions ] = await Promise.all([
        projectApi.getGeospatialAnalytics({ projectId, componentId }),
        projectApi.getGeospatialOptions({ projectId, componentId }),
      ]);
      setGeospatialAnalytic(geospatialAnalytic);
      setGeospatialOptions(geospatialOptions);
    },
  });

  useEffect(() => {
    fetchGeospatialAnalytics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  const indexAnalytics = useMemo(() => geospatialAnalytic.find(g => !landUseWater.includes(g.geoInfoTypeId)), [ geospatialAnalytic ]);
  const landUseWaterAnalytics = useMemo(() => geospatialAnalytic.find(g => landUseWater.includes(g.geoInfoTypeId)), [ geospatialAnalytic ]);

  return (
    <Grid container>
      <TitleContainer
        maxWidth='xl'
        project={project}
        breadcrumbs={[ 'projects', 'project', 'sites', 'site', { name: 'Agua dulce' } ]}
      >
        Estado de la naturaleza - Agua dulce
      </TitleContainer>

      <ContentContainer maxWidth='xl'>
        <Grid container spacing={1} item>
          <Grid item xs={12}>
            <AnalyticCard title='Indicadores'>
              <Divider />
              <Box my={0.5} />
              {isLoadingConcerns && <LoadingBox label='indicadores de agua dulce' />}
              {isSuccessConcerns &&
                <Box display='flex' flexDirection='column' alignItems='center'>
                  {concerns.map(({ name, level, value, isPartial, tooltip, url, ReasonComponent }, index) =>
                    <Fragment key={`concern-${index}`}>
                      <ConcernSummary title={name} level={level} value={value} isPartial={isPartial} tooltip={tooltip} url={url}>
                        Nivel de preocupación <strong>{level.name}</strong> para <ReasonComponent />
                      </ConcernSummary>
                      {(concerns.length - 1) !== index && <Divider className={classes.divider} />}
                    </Fragment>,
                  )}
                </Box>
              }
            </AnalyticCard>
          </Grid>

          <GeospatialAnalytic
            title="Índices espectrales"
            analytic={indexAnalytics}
            interestArea={interestArea.data}
            getLeafletOptions={bounds => getLeafletOptionsFn({ token, bounds })}
            geospatialTypesOptions={geospatialOptions.spectralIndicesOptions}
            fetchStatus={{ isLoading: isLoadingGeospatialAnalytics, isSuccess: isSuccessGeospatialAnalytics }}
          />

          <GeospatialAnalytic
            id="landcover-water"
            title="Cuerpos de agua"
            analytic={landUseWaterAnalytics}
            interestArea={interestArea.data}
            getLeafletOptions={bounds => getLeafletOptionsFn({ token, bounds })}
            geospatialTypesOptions={geospatialOptions.landUseWaterOptions}
            fetchStatus={{ isLoading: isLoadingGeospatialAnalytics, isSuccess: isSuccessGeospatialAnalytics }}
          />

          {/* <MonitoringPlanAnalytics /> */}

        </Grid>
      </ContentContainer>
    </Grid>
  );
};

FreshwaterContainer.propTypes = {
  interestArea: PropTypes.shape({
    data: PropTypes.array.isRequired,
    fetchStatus: PropTypes.object,
  }).isRequired,
  project: PropTypes.shape({
    siteName: PropTypes.string,
    siteId: PropTypes.number,
    name: PropTypes.string,
    id: PropTypes.number,
  }),
};


export { FreshwaterContainer };
